/**
 * E5.2: which drawn tree stands at a flora entry, so what blocks follows what is drawn. The stands (rendering/
 * treeStands.ts) draw each normal tree as one of a few seeded variants of its species, turned by a yaw about its up
 * and, once felled, its trunk lying along a fall direction — all hashed from its id, never the sim's random streams.
 * Those draws live here so the sim (core/surfaceWalk.ts's tree pieces) and the drawing read the same numbers; and
 * the model's frame on the ground is the drawing's (a model's +y along the tree's up, by the shortest turn, then the yaw
 * about it). Pure; integer hashing and dmath only.
 */
import type { Vec3 } from '../types';
import { dsin, dcos, datan2 } from './dmath';

/** How many seeded variants of each tree the stands draw (rendering/treeStands.ts TREE_LOD.variants). */
export const TREE_VARIANTS = 3;

/** A deterministic hash of (n, salt) in [0, 1): the stands' per-prop draws (variant, yaw, fall). */
export function propHash(n: number, salt: number): number {
  let h = (Math.imul(n | 0, 0x9e3779b1) ^ Math.imul(salt, 0x85ebca77)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** The drawn variant of a tree (0 … TREE_VARIANTS − 1). */
export function treeVariant(id: number): number { return Math.floor(propHash(id, 1) * TREE_VARIANTS); }
/** The drawn yaw of a standing tree about its up (radians). */
export function treeYaw(id: number): number { return propHash(id, 2) * Math.PI * 2; }
/** The drawn variant of a normal (biome) rock (0 … TREE_VARIANTS − 1): its prototype's (66.9: the hull the cover slots read). */
export function rockVariant(id: number): number { return Math.floor(propHash(id, 3) * TREE_VARIANTS); }
/** The drawn yaw of a normal rock about its up (radians): the turn of its hull on the ground (66.9). */
export function rockYaw(id: number): number { return propHash(id, 4) * Math.PI * 2; }

/**
 * Model space (x right, y up the tree, z) to world directions for a tree standing along `up` (unit) turned by `yaw`:
 * the yaw about the model's y, then the shortest turn of +y onto `up` (as THREE's setFromUnitVectors, which the
 * stands use; straight down turns about x).
 */
export function treeFrame(up: Vec3, yaw: number): (x: number, y: number, z: number) => Vec3 {
  const cy = dcos(yaw), sy = dsin(yaw);
  const c = up.y;
  return (x0, y, z0) => {
    const x = x0 * cy + z0 * sy, z = -x0 * sy + z0 * cy;
    if (c < -1 + 1e-9) return { x, y: -y, z: -z };
    // R v = v + w × v + w × (w × v) / (1 + c), w = (0,1,0) × up = (up.z, 0, −up.x)
    const wx = up.z, wz = -up.x;
    const cx = -wz * y, cyy = wz * x - wx * z, cz = wx * y;          // w × v
    const k = 1 / (1 + c);
    const ccx = -wz * cyy, ccy = wz * cx - wx * cz, ccz = wx * cyy;   // w × (w × v)
    return { x: x + cx + ccx * k, y: y + cyy + ccy * k, z: z + cz + ccz * k };
  };
}

/**
 * The fall of a felled tree, in its model frame's yaw (0 = along +z): away from where the blow came from (`from`,
 * world) seen from its foot (`foot`, world) on the ground plane of `up`, or a hashed way when nothing says (as the
 * stands draw it).
 */
export function treeFallYaw(id: number, up: Vec3, foot: Vec3, from: Vec3 | null | undefined): number {
  if (from) {
    const px = foot.x - from.x, py = foot.y - from.y, pz = foot.z - from.z;
    // into the model frame with no yaw: the inverse (transpose) of the shortest turn
    const toWorld = treeFrame(up, 0);
    const ex = toWorld(1, 0, 0), ez = toWorld(0, 0, 1);
    const lx = px * ex.x + py * ex.y + pz * ex.z, lz = px * ez.x + py * ez.y + pz * ez.z;
    if (lx * lx + lz * lz > 1e-18) return datan2(lx, lz);
  }
  return propHash(id, 5) * Math.PI * 2;
}
